import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, CreditCard, Zap, Star, Shield, Clock, Users, Database, Sparkles } from 'lucide-react';
import { useSubscription } from '../lib/hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { CreativePricingDemo } from '../components/ui/creative-pricing-demo';

const Subscription = () => {
  const { plans, currentSubscription, loading, createCheckoutSession } = useSubscription();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);

  // Check URL parameters for Stripe checkout status
  useEffect(() => {
    const url = new URL(window.location.href);
    const success = url.searchParams.get('success');
    const canceled = url.searchParams.get('canceled');
    
    if (success) {
      setShowSuccess(true);
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (canceled) {
      setShowCanceled(true);
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSubscribe = async (planId: string) => {
    setCheckoutLoading(planId);
    setCheckoutError(null);
    
    try {
      const { url, error } = await createCheckoutSession(planId);
      
      if (error) {
        setCheckoutError(error);
        return;
      }
      
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Failed to create checkout session');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const response = await fetch('/.netlify/functions/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      
      // Refresh subscription data
      window.location.reload();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert(err instanceof Error ? err.message : 'Failed to cancel subscription');
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-gray-900 dark:text-white"
          >
            Subscription Plans
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-xl text-gray-600 dark:text-gray-400"
          >
            Choose the plan that's right for you
          </motion.p>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Subscription Successful
                </h3>
                <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                  <p>
                    Thank you for your subscription! Your account has been upgraded.
                  </p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <button
                      onClick={() => setShowSuccess(false)}
                      className="px-2 py-1.5 rounded-md text-sm font-medium text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Canceled Message */}
        {showCanceled && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Checkout Canceled
                </h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    Your checkout process was canceled. No charges were made.
                  </p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <button
                      onClick={() => setShowCanceled(false)}
                      className="px-2 py-1.5 rounded-md text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {checkoutError && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Checkout Error
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>{checkoutError}</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <button
                      onClick={() => setCheckoutError(null)}
                      className="px-2 py-1.5 rounded-md text-sm font-medium text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Current Subscription Status */}
        {!loading && currentSubscription && currentSubscription.plan !== 'free' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-12 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-6"
          >
            <h2 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-4">
              Current Subscription
            </h2>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  <span className="font-medium">Plan:</span> {currentSubscription.plan.charAt(0).toUpperCase() + currentSubscription.plan.slice(1)}
                </p>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  <span className="font-medium">Status:</span> {currentSubscription.status.charAt(0).toUpperCase() + currentSubscription.status.slice(1)}
                </p>
                {currentSubscription.periodEnd && (
                  <p className="text-sm text-indigo-700 dark:text-indigo-300">
                    <span className="font-medium">Renews on:</span> {new Date(currentSubscription.periodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
              >
                Cancel Subscription
              </button>
            </div>
            <p className="text-xs text-indigo-600 dark:text-indigo-400">
              Note: Canceling your subscription will allow you to use your current plan until the end of the billing period.
            </p>
          </motion.div>
        )}

        {/* Creative Pricing Component */}
        <div className="mb-16">
          <CreativePricingDemo />
        </div>

        {/* Traditional Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: plans.indexOf(plan) * 0.1 }}
              className={`bg-white dark:bg-dark-800 rounded-xl shadow-lg border ${
                plan.name === currentSubscription.plan
                  ? 'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-500 dark:ring-indigo-400'
                  : 'border-gray-200 dark:border-dark-700'
              } overflow-hidden`}
            >
              {plan.name === currentSubscription.plan && (
                <div className="bg-indigo-500 text-white text-center py-1 text-sm font-medium">
                  Current Plan
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.name.charAt(0).toUpperCase() + plan.name.slice(1)}
                </h3>
                <div className="flex items-baseline mb-6">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">${plan.price}</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">/month</span>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {plan.features.storage === -1 ? 'Unlimited' : plan.features.storage}GB Storage
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {plan.features.social_accounts === -1 ? 'Unlimited' : plan.features.social_accounts} Social Accounts
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {plan.features.ai_credits === -1 ? 'Unlimited' : plan.features.ai_credits} AI Credits
                    </span>
                  </li>
                  <li className="flex items-start">
                    {plan.features.analytics ? (
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-gray-600 dark:text-gray-300">
                      Advanced Analytics
                    </span>
                  </li>
                  <li className="flex items-start">
                    {plan.features.advanced_tools ? (
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-gray-600 dark:text-gray-300">
                      Advanced Tools
                    </span>
                  </li>
                </ul>
                <button
                  onClick={() => handleSubscribe(plan.name)}
                  disabled={plan.name === currentSubscription.plan || checkoutLoading !== null}
                  className={`w-full py-2 px-4 rounded-lg font-medium ${
                    plan.name === currentSubscription.plan
                      ? 'bg-gray-300 dark:bg-dark-700 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                      : checkoutLoading === plan.name
                      ? 'bg-indigo-400 text-white cursor-wait'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 transition-colors'
                  }`}
                >
                  {plan.name === currentSubscription.plan
                    ? 'Current Plan'
                    : checkoutLoading === plan.name
                    ? 'Processing...'
                    : 'Subscribe'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features Comparison */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
            Features Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-200 dark:border-dark-700">
              <thead>
                <tr className="bg-gray-50 dark:bg-dark-700">
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Feature
                  </th>
                  <th className="py-4 px-6 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    Free
                  </th>
                  <th className="py-4 px-6 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    Charged
                  </th>
                  <th className="py-4 px-6 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    Supercharged
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                <tr>
                  <td className="py-4 px-6 text-sm text-gray-900 dark:text-white flex items-center">
                    <Database className="h-5 w-5 text-indigo-500 mr-2" />
                    Storage
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    5GB
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    50GB
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    Unlimited
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-sm text-gray-900 dark:text-white flex items-center">
                    <Users className="h-5 w-5 text-indigo-500 mr-2" />
                    Social Accounts
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    2
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    5
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    Unlimited
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-sm text-gray-900 dark:text-white flex items-center">
                    <Sparkles className="h-5 w-5 text-indigo-500 mr-2" />
                    AI Credits
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    10/month
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    100/month
                  </td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600 dark:text-gray-300">
                    Unlimited
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-sm text-gray-900 dark:text-white flex items-center">
                    <CreditCard className="h-5 w-5 text-indigo-500 mr-2" />
                    Advanced Analytics
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-sm text-gray-900 dark:text-white flex items-center">
                    <Zap className="h-5 w-5 text-indigo-500 mr-2" />
                    Advanced Tools
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-sm text-gray-900 dark:text-white flex items-center">
                    <Shield className="h-5 w-5 text-indigo-500 mr-2" />
                    Priority Support
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Can I change my plan later?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be applied immediately, and any price difference will be prorated.
              </p>
            </div>
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                How do AI credits work?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                AI credits are used when you generate content using our AI tools. Each generation uses a certain number of credits depending on the complexity of the task. Credits reset at the beginning of each billing cycle.
              </p>
            </div>
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                What happens if I cancel my subscription?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                If you cancel your subscription, you'll continue to have access to your current plan until the end of your billing period. After that, your account will be downgraded to the free plan.
              </p>
            </div>
            <div className="bg-white dark:bg-dark-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Is there a refund policy?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We offer a 14-day money-back guarantee for all paid plans. If you're not satisfied with our service, contact our support team within 14 days of your purchase for a full refund.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;